// One feedback request in the console: the whole thread with internal notes, the files (internal ones marked),
// status / estimate / title / area, comment / ask / internal note with files, sharing a note, combining,
// and staff-only links to related requests from any firm. Every action that notifies the firm says who, next to
// its button, and the button carries a bell (Notify.tsx). Messages can be formatted (Formatting.tsx).
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Bell, ChevronLeft, ChevronRight, Download, EyeOff, Link2, ListChecks, Lock, Maximize2, MessageSquare, Paperclip, Share2, Undo2, Unlink, X, Zap } from 'lucide-react';
import { useMe } from '../AdminShell';
import { usePortalSettings } from '../portalSettings';
import { NewItemDialog } from '../backlog/NewItemDialog';
import { api, ApiError, authenticatedBlob } from '../../lib/api';
import { fmtDateTime } from '../../lib/format';
import { Banner, Button, Card, Dialog, Pill, Select, Spinner, TextArea, TextField, cx } from '../../ui';
import { FEEDBACK_STATUS_REASON_MAX, feedbackPatchNotice, mentionsToPlain, notifiesByDefault, type FeedbackPatchNotice, type FeedbackUpdatePart, type PortalSettings } from '@upm/domain';
import { audience, NotifyNote, NotifyPicker, NotifyToggle, threadPeople, type NotifyPerson } from './Notify';
import { MessageBody, useFormatting } from './Formatting';
import { useMentionPicker, type Teammate } from '../Mentions';
import { NotifyPreview } from './NotifyPreview';
import { AutofixPanel } from './Autofix';
import {
  AREAS, KIND_LABEL, STATUS_LABEL, STATUS_TONE, URGENCY_LABEL, firmLabel, fmtCalendarDate,
  type Attachment, type FeedbackStatus, type Message, type QueueRow, type RequestDetail,
} from './types';

const msg = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);
const base = (id: string) => `/api/admin/feedback/${id}`;

export function FeedbackDetailPage() {
  const { id = '' } = useParams();
  const [request, setRequest] = useState<RequestDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { setRequest((await api.get<{ request: RequestDetail }>(base(id))).request); setError(null); }
    catch (e) { setError(msg(e, 'Could not load this request.')); }
  }, [id]);
  useEffect(() => { void load(); }, [load]);
  // Opening a request marks what the page shows as read for you (not a reply that lands after), and updates
  // the Feedback badge.
  const { reportFeedbackSeen, reportWorkSeen } = useMe();
  const shown = request?.id === id ? request.last_sequence : null;
  useEffect(() => {
    if (!shown) return;
    api.post(`${base(id)}/seen`, { up_to: shown }).then(() => { reportFeedbackSeen(); reportWorkSeen(); }, () => {});
  }, [id, shown, reportFeedbackSeen, reportWorkSeen]);

  if (!request) return error ? <Banner tone="error" role="alert">{error}</Banner> : <Spinner label="Loading request" />;
  const filesOn = (messageId: string | null) => request.attachments.filter((a) => a.message_id === messageId);

  return (
    <div className="mx-auto flex w-full max-w-shell flex-col gap-5">
      <Link to="/admin/feedback" className="inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-brand no-underline">
        <ArrowLeft aria-hidden size={16} /> Feedback
      </Link>
      {error && <Banner tone="error" role="alert">{error}</Banner>}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-5">
          <Card className="flex flex-col gap-3 p-6">
            <div className="flex flex-wrap items-center gap-2 text-[13px] text-muted">
              <span className="font-bold text-brand">{request.reference}</span>
              <span>{KIND_LABEL[request.kind]}</span>
              {request.urgency && <span>· {URGENCY_LABEL[request.urgency]}</span>}
              <span>· {firmLabel(request)}</span>
              {request.reopened_at && <Pill tone="warn">Reopened {request.reopen_count}×</Pill>}
            </div>
            <h1 className="m-0 text-[22px] font-bold leading-tight">{request.title}</h1>
            <p className="m-0 text-[13px] text-muted">{request.submitted_by_name} · sent {fmtDateTime(request.submitted_at)}</p>
            <p className="m-0 whitespace-pre-line leading-relaxed">{request.body}</p>
            <Files files={filesOn(null)} requestId={request.id} onError={setError} />
          </Card>

          <Card className="overflow-hidden p-0">
            <h2 className="m-0 border-b border-line-soft px-6 py-4 text-base font-bold">Conversation</h2>
            {request.messages.length === 0 && <p className="m-0 px-6 py-5 text-muted">No messages yet.</p>}
            <ol className="m-0 list-none divide-y divide-line-soft p-0">
              {request.messages.map((m) => (
                <MessageItem key={m.id} m={m} files={filesOn(m.id)} requestId={request.id} thread={threadPeople(request)} onChanged={load} onError={setError} />
              ))}
            </ol>
            <Composer request={request} onSent={load} />
          </Card>
        </div>

        <aside className="flex flex-col gap-5">
          <AutofixPanel request={request} onSaved={setRequest} onError={setError} />
          <StatusPanel request={request} onSaved={setRequest} onError={setError} />
          <BacklogPanel request={request} onChanged={load} />
          <CombinePanel request={request} onChanged={load} onError={setError} />
          <LinksPanel request={request} onSaved={setRequest} onError={setError} />
          <Card className="flex flex-col gap-1.5 p-5 text-sm">
            <h2 className="m-0 mb-1 text-[15px] font-bold">People</h2>
            <p className="m-0">{request.submitted_by_name} <span className="text-muted">(sent it)</span></p>
            {request.notify_whole_firm && <p className="m-0 text-[13px] text-muted">Everyone in the firm hears when it's resolved</p>}
            {request.watchers.map((w) => <p key={w.id} className="m-0">{w.name}</p>)}
          </Card>
          {Object.keys(request.diagnostics ?? {}).length > 0 && (
            <Card className="p-5 text-sm">
              <details>
                <summary className="cursor-pointer text-[15px] font-bold">Page and browser details</summary>
                <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-all text-xs text-ink-2">{JSON.stringify(request.diagnostics, null, 2)}</pre>
              </details>
            </Card>
          )}
          <Card className="p-5 text-sm">
            <details>
              <summary className="cursor-pointer text-[15px] font-bold">Activity</summary>
              <ul className="m-0 mt-2 flex list-none flex-col gap-2 p-0">
                {request.events.map((e) => (
                  <li key={e.id} className="text-ink-2">
                    <span className="font-semibold text-ink">{e.actor_name ?? (e.actor_kind === 'firm_user' ? 'Firm user' : e.actor_kind)}</span> {e.type.replace(/_/g, ' ')}
                    {e.payload?.silent && <span className="text-muted"> (no notification)</span>}
                    <span className="block text-xs text-muted">{fmtDateTime(e.at)}</span>
                  </li>
                ))}
              </ul>
            </details>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function Files({ files, requestId, onError }: { files: Attachment[]; requestId: string; onError: (m: string) => void }) {
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [viewing, setViewing] = useState<number | null>(null);
  const key = files.map((f) => `${f.id}:${f.scan}`).join(',');
  useEffect(() => {
    let cancelled = false;
    const urls: string[] = [];
    void Promise.all(files.filter((f) => f.is_screenshot && f.scan === 'clean').map(async (f) => {
      try { return [f.id, URL.createObjectURL(await authenticatedBlob(`${base(requestId)}/attachments/${f.id}/content`))] as const; }
      catch { return null; }
    })).then((pairs) => {
      const entries = pairs.filter((p): p is readonly [string, string] => p !== null);
      if (cancelled) entries.forEach(([, url]) => URL.revokeObjectURL(url));
      else { urls.push(...entries.map(([, url]) => url)); setPreviews(Object.fromEntries(entries)); }
    });
    return () => { cancelled = true; urls.forEach(URL.revokeObjectURL); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, requestId]);
  if (files.length === 0) return null;
  const download = async (f: Attachment) => {
    try { window.open((await api.get<{ url: string }>(`${base(requestId)}/attachments/${f.id}/download`)).url, '_blank', 'noopener'); }
    catch (e) { onError(msg(e, 'Could not download the file.')); }
  };
  const shown = files.filter((f) => previews[f.id]);
  return (
    <>
    <ul className="m-0 flex list-none flex-wrap gap-2.5 p-0">
      {files.map((f) => (
        <li key={f.id} className="w-44 overflow-hidden rounded-[10px] border border-line bg-panel text-[13px]">
          {previews[f.id]
            ? (
              <button type="button" aria-label={`Open ${f.filename}`} onClick={() => setViewing(shown.indexOf(f))}
                className="block w-full cursor-zoom-in border-0 bg-transparent p-0">
                <img src={previews[f.id]} alt={f.source_title || f.filename} className="block h-24 w-full object-cover" />
              </button>
            )
            : <div className="flex h-24 items-center justify-center text-muted"><Paperclip aria-hidden size={20} /></div>}
          <div className="flex items-center gap-1.5 px-2.5 py-2">
            {f.visibility === 'internal' && <EyeOff aria-label="Hidden from the firm" size={14} className="flex-none text-warn-fg" />}
            <span className="min-w-0 flex-1 truncate" title={f.filename}>{f.filename}</span>
            {f.scan === 'clean'
              ? <button type="button" aria-label={`Download ${f.filename}`} onClick={() => void download(f)} className="flex h-8 w-8 items-center justify-center rounded-md border-0 bg-transparent text-muted hover:bg-subtle hover:text-ink"><Download aria-hidden size={15} /></button>
              : <Pill tone={f.scan === 'pending' ? 'neutral' : 'bad'}>{f.scan === 'pending' ? 'Scanning' : 'Blocked'}</Pill>}
          </div>
        </li>
      ))}
    </ul>
    {viewing !== null && shown[viewing] && (
      <ImageViewer files={shown} previews={previews} index={viewing} onIndex={setViewing} onClose={() => setViewing(null)} onDownload={(f) => void download(f)} />
    )}
    </>
  );
}

/** A screenshot at full size over the page: Previous / Next between this message's screenshots, Download, Close. */
function ImageViewer({ files, previews, index, onIndex, onClose, onDownload }: {
  files: Attachment[]; previews: Record<string, string>; index: number; onIndex: (i: number) => void; onClose: () => void; onDownload: (f: Attachment) => void;
}) {
  const f = files[index]!;
  const close = useRef<HTMLButtonElement>(null);
  const go = useCallback((d: number) => onIndex((index + d + files.length) % files.length), [files.length, index, onIndex]);
  // Focus moves into the viewer, and back to what opened it on close.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    close.current?.focus();
    return () => opener?.focus();
  }, []);
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      if (files.length > 1 && e.key === 'ArrowLeft') go(-1);
      if (files.length > 1 && e.key === 'ArrowRight') go(1);
    };
    window.addEventListener('keydown', key);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', key); document.body.style.overflow = overflow; };
  }, [files.length, go, onClose]);
  const dark = '!border-[#3A4556] !bg-[#1B222C] !text-[#E6EAF0] hover:!bg-[#263040]';
  const arrow = 'absolute top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-[#3A4556] bg-[#1B222C] text-white';
  // Tab stays inside the viewer.
  const trap = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const all = [...e.currentTarget.querySelectorAll<HTMLElement>('button')];
    const first = all[0];
    const last = all.at(-1);
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
  };
  return (
    <div role="dialog" aria-modal="true" aria-label={`${f.filename}, ${index + 1} of ${files.length}`} onKeyDown={trap} className="fixed inset-0 z-50 flex flex-col bg-[#11161D] text-white">
      <header className="flex flex-wrap items-center gap-2.5 border-b border-[#263040] px-5 py-3">
        <div className="flex min-w-0 flex-1 flex-col leading-tight">
          <span className="truncate font-bold">{f.filename}{files.length > 1 && <span className="font-medium text-[#AEB8CC]"> · {index + 1} of {files.length}</span>}</span>
          {f.source_title && <span className="truncate text-xs text-[#AEB8CC]">{f.source_title}</span>}
        </div>
        <Button size="sm" className={dark} onClick={() => onDownload(f)}><Download aria-hidden size={15} /> Download</Button>
        <Button ref={close} size="sm" className={dark} onClick={onClose}><X aria-hidden size={15} /> Close</Button>
      </header>
      {/* Clicking the dark space around the image closes it. */}
      <div className="relative flex min-h-0 flex-1 items-center justify-center p-5" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <img src={previews[f.id]} alt={f.source_title || f.filename} className="max-h-full max-w-full rounded-md bg-white object-contain shadow-[0_20px_50px_rgba(0,0,0,0.5)]" />
        {files.length > 1 && <button type="button" aria-label="Previous" onClick={() => go(-1)} className={cx(arrow, 'left-4')}><ChevronLeft aria-hidden size={18} /></button>}
        {files.length > 1 && <button type="button" aria-label="Next" onClick={() => go(1)} className={cx(arrow, 'right-4')}><ChevronRight aria-hidden size={18} /></button>}
      </div>
    </div>
  );
}

const KIND_TEXT: Record<Message['kind'], string> = { comment: 'Comment', question: 'Question', reply: 'Reply', internal_note: 'Internal note' };

function MessageItem({ m, files, requestId, thread, onChanged, onError }: {
  m: Message; files: Attachment[]; requestId: string; thread: NotifyPerson[]; onChanged: () => Promise<void>; onError: (s: string) => void;
}) {
  const [sharing, setSharing] = useState(false);
  // The firm never sees tags, so sharing starts from the note with "@Name" only.
  const plain = mentionsToPlain(m.body);
  const [text, setText] = useState(plain);
  const [chosen, setChosen] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [notifyOff, setNotifyOff] = useState<string[]>([]);
  const notifyTo = thread.map((p) => p.id).filter((id) => !notifyOff.includes(id));
  const box = useRef<HTMLTextAreaElement>(null);
  const formatting = useFormatting(box, setText);
  const internal = m.visibility === 'internal';
  const share = async () => {
    setBusy(true);
    try {
      await api.post(`${base(requestId)}/messages/${m.id}/share`, { ...(text.trim() !== plain ? { body: text.trim() } : {}), attachment_ids: chosen, notify_users: notifyTo });
      setSharing(false);
      await onChanged();
    } catch (e) { onError(msg(e, 'Could not share the note.')); } finally { setBusy(false); }
  };
  return (
    <li className={cx('flex flex-col gap-1.5 px-6 py-4', internal && 'bg-warn-tint')}>
      <div className="flex flex-wrap items-center gap-2 text-[13px] text-muted">
        <span className="font-semibold text-ink">{m.author_name}</span>
        <span>{KIND_TEXT[m.kind]}</span>
        {internal && <span className="inline-flex items-center gap-1 font-semibold text-warn-fg"><Lock aria-hidden size={13} /> Hidden from the firm</span>}
        {m.kind === 'question' && (m.answered_at ? <Pill tone="ok">Answered</Pill> : <Pill tone="warn">Waiting for answer</Pill>)}
        {m.shared_from_note && <span>· shared from an internal note</span>}
        <span className="ml-auto">{fmtDateTime(m.created_at)}</span>
      </div>
      <MessageBody text={m.body} />
      <Files files={files} requestId={requestId} onError={onError} />
      {internal && !sharing && (
        <Button variant="link" size="sm" className="w-fit" onClick={() => { setNotifyOff([]); setSharing(true); }}><Share2 aria-hidden size={14} /> Share with requester</Button>
      )}
      {sharing && (
        <div className="mt-1 flex flex-col gap-3 rounded-[10px] border border-line bg-white p-4">
          <p className="m-0 text-[13px] text-muted">Posts a comment the requester and their notify list can see. The note stays internal.</p>
          <TextArea ref={box} label="What they'll see" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={formatting.onKeyDown} rows={3} />
          {formatting.bar}
          {files.length > 0 && (
            <fieldset className="m-0 flex flex-col gap-1.5 border-0 p-0 text-sm">
              <legend className="mb-1 p-0 font-semibold">Files to share (the rest stay hidden)</legend>
              {files.map((f) => (
                <label key={f.id} className="flex items-center gap-2">
                  <input type="checkbox" className="h-4 w-4" checked={chosen.includes(f.id)}
                    onChange={(e) => setChosen((c) => (e.target.checked ? [...c, f.id] : c.filter((x) => x !== f.id)))} />
                  {f.filename}
                </label>
              ))}
            </fieldset>
          )}
          <NotifyPicker people={thread} what="the comment, as a new message on their request" chosen={notifyTo}
            onChange={(ids) => setNotifyOff(thread.map((p) => p.id).filter((id) => !ids.includes(id)))} />
          <div className="flex gap-2">
            <Button variant="primary" size="sm" disabled={busy || !text.trim()} onClick={() => void share()}>{notifyTo.length > 0 && <Bell aria-hidden size={14} />} Share</Button>
            <Button variant="outline" size="sm" onClick={() => setSharing(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </li>
  );
}

type ComposerKind = 'comment' | 'question' | 'internal_note';
const HINT: Record<ComposerKind, string> = {
  comment: 'The requester and their notify list see this.',
  question: 'Only the requester is asked. It stays marked Reply needed in their Communications stack until they answer.',
  internal_note: 'Only the platform team sees this and any files on it. Type @ to tag a teammate; it shows in their My work.',
};

/** What a question notifies: the reminder follows Settings → Automatic reminders (unknown while it loads). */
export function questionNote(settings: PortalSettings | null): string {
  if (!settings) return 'your question';
  return settings.feedbackQuestionReminders === true
    ? "your question, then one reminder after 3 business days if it's unanswered"
    : 'your question. No automatic reminder: feedback question reminders are off in Settings';
}

function Composer({ request, onSent }: { request: RequestDetail; onSent: () => Promise<void> }) {
  const { settings } = usePortalSettings();
  const requestId = request.id;
  const [kind, setKind] = useState<ComposerKind>('comment');
  // Who staff unticked; anyone added to the notify list later starts ticked.
  const [notifyOff, setNotifyOff] = useState<string[]>([]);
  const people = threadPeople(request);
  const notifyTo = people.map((p) => p.id).filter((id) => !notifyOff.includes(id));
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const formatting = useFormatting(box, setText);
  // Only an internal note can tag a teammate.
  const [team, setTeam] = useState<Teammate[]>([]);
  useEffect(() => { api.get<{ team: Teammate[] }>('/api/admin/work/team').then((r) => setTeam(Array.isArray(r?.team) ? r.team : []), () => {}); }, []);
  const mentions = useMentionPicker(box, setText, kind === 'internal_note' ? team : []);
  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const form = new FormData();
      form.set('kind', kind);
      form.set('body', kind === 'internal_note' ? mentions.tagged(text.trim()) : text.trim());
      if (kind === 'comment') form.set('notify_users', JSON.stringify(notifyTo));
      files.forEach((f) => form.append(f.type.startsWith('image/') ? 'screenshots' : 'files', f, f.name));
      await api.post(`${base(requestId)}/messages`, form);
      setText('');
      setFiles([]);
      setNotifyOff([]);
      await onSent();
    } catch (e) { setError(msg(e, 'Could not add the message.')); } finally { setBusy(false); }
  };
  return (
    <div className={cx('flex flex-col gap-3 border-t border-line-soft px-6 py-5', kind === 'internal_note' && 'bg-warn-tint')}>
      <div role="tablist" aria-label="Message type" className="flex flex-wrap gap-1.5">
        {(['comment', 'question', 'internal_note'] as ComposerKind[]).map((k) => (
          <button key={k} type="button" role="tab" aria-selected={kind === k} onClick={() => setKind(k)}
            className={cx('inline-flex min-h-9 items-center gap-1.5 rounded-pill border px-3.5 text-[13px] font-semibold',
              kind === k ? 'border-brand bg-brand text-white' : 'border-line-btn bg-white text-ink hover:border-brand')}>
            {k === 'internal_note' ? <Lock aria-hidden size={13} /> : <MessageSquare aria-hidden size={13} />}
            {KIND_TEXT[k]}
          </button>
        ))}
      </div>
      <p className="m-0 text-[13px] text-muted">{HINT[kind]}</p>
      <div className="relative">
        <TextArea ref={box} label="Message" value={text} onChange={(e) => { setText(e.target.value); mentions.sync(); }} rows={3}
          {...mentions.inputProps} onKeyDown={(e) => { if (!mentions.onKeyDown(e)) formatting.onKeyDown(e); }} />
        {mentions.menu}
      </div>
      {formatting.bar}
      {files.length > 0 && (
        <ul className="m-0 flex list-none flex-wrap gap-2 p-0 text-[13px]">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="inline-flex items-center gap-1 rounded-pill bg-subtle px-3 py-1">
              {f.name}
              <button type="button" aria-label={`Remove ${f.name}`} onClick={() => setFiles((c) => c.filter((_, j) => j !== i))} className="border-0 bg-transparent text-muted">×</button>
            </li>
          ))}
        </ul>
      )}
      {error && <Banner tone="error" role="alert">{error}</Banner>}
      {kind === 'comment' && <NotifyPicker people={people} what="your message" chosen={notifyTo}
        onChange={(ids) => setNotifyOff(people.map((p) => p.id).filter((id) => !ids.includes(id)))} />}
      {kind === 'question' && <NotifyNote to={audience(request, 'requester')} what={questionNote(settings)} />}
      {kind === 'internal_note' && <NotifyNote to={null} />}
      <div className="flex items-center gap-2">
        <input ref={input} type="file" multiple className="hidden" onChange={(e) => { const selected = Array.from(e.target.files ?? []); setFiles((c) => [...c, ...selected]); e.target.value = ''; }} />
        <Button variant="outline" size="sm" onClick={() => input.current?.click()}><Paperclip aria-hidden size={14} /> Attach</Button>
        <Button variant="primary" size="sm" className="ml-auto" disabled={busy || !text.trim()} onClick={() => void send()}>
          {(kind === 'question' || (kind === 'comment' && notifyTo.length > 0)) && <Bell aria-hidden size={14} />}
          {kind === 'internal_note' ? 'Add internal note' : kind === 'question' ? 'Ask' : 'Send'}
        </Button>
      </div>
    </div>
  );
}

const PART_TEXT: Record<FeedbackUpdatePart, (s: FeedbackStatus) => string> = {
  status: (s) => `status ${STATUS_LABEL[s]}`, estimate_removed: () => 'estimate removed', title: () => 'the new title', area: () => 'the new area',
};

/**
 * What saving these changes tells the firm, by the same rule the API's PATCH uses (@upm/domain feedback.ts):
 * a resolution, a new estimate, or the other changes as one update. Null: nobody hears. `on` is the Notify
 * default: on for a resolution or an estimate, off for a minor update.
 */
export function saveNotice(r: RequestDetail, changes: Record<string, unknown>): { to: string[]; what: string; kind: 'resolved' | 'estimate' | 'update'; on: boolean; notice: FeedbackPatchNotice } | null {
  const next = (changes.status as FeedbackStatus | undefined) ?? r.status;
  const n = feedbackPatchNotice({
    from: r.status, to: next, estimateChanged: 'estimated_completion' in changes,
    estimate: (changes.estimated_completion as string | null | undefined) ?? null, title: 'title' in changes, area: 'area' in changes,
  });
  if (!n) return null;
  const on = notifiesByDefault(n);
  if (n.kind === 'resolved') return { kind: n.kind, notice: n, on, to: audience(r, 'resolved'), what: n.status === 'done' ? 'marked Done' : 'Won’t do, with your reason' };
  if (n.kind === 'estimate') return { kind: n.kind, notice: n, on, to: audience(r, 'thread'), what: `estimated completion ${fmtCalendarDate(n.date)}` };
  return { kind: n.kind, notice: n, on, to: audience(r, 'thread'), what: n.parts.map((p) => PART_TEXT[p](next)).join(', ') };
}

function StatusPanel({ request, onSaved, onError }: { request: RequestDetail; onSaved: (r: RequestDetail) => void; onError: (m: string) => void }) {
  const [status, setStatus] = useState<FeedbackStatus>(request.status);
  const [reason, setReason] = useState(request.status_reason ?? '');
  const [estimate, setEstimate] = useState(request.estimated_completion ?? '');
  const [title, setTitle] = useState(request.title);
  const [area, setArea] = useState(request.area ?? '');
  const [busy, setBusy] = useState(false);
  /** The Notify choice, for the kind of change it was made on; a different kind of change gets its own default. */
  const [notifyChoice, setNotifyChoice] = useState<{ kind: string; on: boolean } | null>(null);
  /** A save that notifies opens "Preview message to client" first; Send and save there makes the PATCH. */
  const [previewing, setPreviewing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    setStatus(request.status); setReason(request.status_reason ?? ''); setEstimate(request.estimated_completion ?? '');
    setTitle(request.title); setArea(request.area ?? ''); setNotifyChoice(null);
  }, [request]);
  const changes = useMemo(() => {
    const c: Record<string, unknown> = {};
    if (status !== request.status) c.status = status;
    if ((reason.trim() || null) !== (request.status_reason ?? null)) c.status_reason = reason.trim();
    if ((estimate || null) !== (request.estimated_completion ?? null)) c.estimated_completion = estimate || null;
    if (title.trim() !== request.title) c.title = title.trim();
    if ((area || null) !== (request.area ?? null)) c.area = area || null;
    return c;
  }, [status, reason, estimate, title, area, request]);
  const needsReason = status === 'wont_do';
  const reasonLength = reason.trim().length;
  const reasonTooLong = reasonLength > FEEDBACK_STATUS_REASON_MAX;
  const changed = Object.keys(changes).length > 0;
  const notice = changed ? saveNotice(request, changes) : null;
  const notify = notice ? (notifyChoice?.kind === notice.kind ? notifyChoice.on : notice.on) : false;
  const save = async () => {
    setBusy(true);
    try { onSaved((await api.patch<{ request: RequestDetail }>(base(request.id), { ...changes, ...(notice ? { notify } : {}) })).request); }
    catch (e) { onError(msg(e, 'Could not save.')); } finally { setBusy(false); setPreviewing(false); }
  };
  const noteHint = reasonLength > FEEDBACK_STATUS_REASON_MAX * 0.8 ? `${reasonLength} of ${FEEDBACK_STATUS_REASON_MAX} characters` : undefined;
  const noteError = reasonTooLong ? `Keep the note to ${FEEDBACK_STATUS_REASON_MAX} characters or fewer. Put the rest in a comment.` : undefined;
  const noteLabel = needsReason ? 'Reason (the requester sees it)' : 'Note for the requester';
  return (
    <Card className="flex flex-col gap-3.5 p-5">
      <div className="flex items-center justify-between">
        <h2 className="m-0 text-[15px] font-bold">Status</h2>
        <Pill tone={STATUS_TONE[request.status]} dot>{STATUS_LABEL[request.status]}</Pill>
      </div>
      {request.status === 'duplicate' ? (
        <p className="m-0 text-sm">
          Duplicate of {request.duplicate_of_id
            ? <Link to={`/admin/feedback/${request.duplicate_of_id}`} className="font-semibold text-brand">{request.duplicate_of_reference}</Link>
            : request.duplicate_of_reference}. Undo the combine to change it.
        </p>
      ) : (
        <>
          <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value as FeedbackStatus)}>
            {(['received', 'planned', 'in_progress', 'done', 'wont_do'] as FeedbackStatus[]).map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
          </Select>
          {(needsReason || status === 'done') && (
            <div className="flex flex-col gap-1">
              <GrowingTextArea label={noteLabel} optional={!needsReason} value={reason} onChange={setReason} hint={noteHint} error={noteError} />
              <Button variant="quiet" size="sm" className="self-end" onClick={() => setExpanded(true)}><Maximize2 aria-hidden size={14} />Expand</Button>
            </div>
          )}
          {status === 'done' && request.status !== 'done' && <p className="m-0 text-[13px] text-muted">They can reopen it as still broken for 14 days.</p>}
        </>
      )}
      <TextField label="Estimated completion" type="date" value={estimate} onChange={(e) => setEstimate(e.target.value)}
        hint={request.estimate_source === 'release' ? 'From the release date. Changing it here makes it a manual estimate.' : undefined} />
      {request.need_by && <p className="m-0 text-[13px] text-muted">Needed by {fmtCalendarDate(request.need_by)}</p>}
      <TextField label="Title" hint={request.title_source === 'generated' ? 'Written for them from the description.' : undefined}
        value={title} onChange={(e) => setTitle(e.target.value)} />
      <Select label="Area" value={area} onChange={(e) => setArea(e.target.value)}>
        <option value="">Not set</option>
        {AREAS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </Select>
      {changed && (notice
        ? <NotifyToggle to={notice.to} what={notice.what} checked={notify} onChange={(on) => setNotifyChoice({ kind: notice.kind, on })} />
        : <NotifyNote to={null} />)}
      <Button variant="primary" disabled={busy || !changed || (needsReason && !reason.trim()) || reasonTooLong} onClick={() => (notify && notice ? setPreviewing(true) : void save())}>
        {notify && <Bell aria-hidden size={16} />}Save changes
      </Button>
      <Dialog open={expanded} title={noteLabel} onClose={() => setExpanded(false)} wide
        footer={<Button variant="primary" onClick={() => setExpanded(false)}>Done</Button>}>
        <TextArea label="Note" className="[&_textarea]:h-[55vh]" value={reason} onChange={(e) => setReason(e.target.value)} hint={noteHint} error={noteError} />
      </Dialog>
      {notice && (
        <NotifyPreview open={previewing} request={request} notice={notice.notice} next={status} reason={reason} to={notice.to}
          busy={busy} onBack={() => setPreviewing(false)} onSend={() => void save()} />
      )}
    </Card>
  );
}

/** A textarea that grows with what is typed, up to half the screen, then scrolls. */
function GrowingTextArea({ label, optional, value, onChange, hint, error }: {
  label: string; optional: boolean; value: string; onChange: (v: string) => void; hint?: string; error?: string;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [value]);
  return <TextArea ref={ref} label={label} optional={optional} value={value} onChange={(e) => onChange(e.target.value)} rows={3}
    hint={hint} error={error} className="[&_textarea]:max-h-[50vh] [&_textarea]:min-h-[84px]" />;
}

function CombinePanel({ request, onChanged, onError }: { request: RequestDetail; onChanged: () => Promise<void>; onError: (m: string) => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [matches, setMatches] = useState<QueueRow[]>([]);
  const [target, setTarget] = useState<QueueRow | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const open = ['received', 'planned', 'in_progress'].includes(request.status);
  useEffect(() => {
    if (!q.trim()) { setMatches([]); return; }
    const t = window.setTimeout(() => {
      const params = new URLSearchParams({ status: '', firm: request.firm_key, cell: request.cell_id, q: q.trim() });
      api.get<{ requests: QueueRow[] }>(`/api/admin/feedback?${params}`)
        .then((r) => setMatches(r.requests.filter((x) => x.id !== request.id && x.status !== 'duplicate').slice(0, 5)), () => setMatches([]));
    }, 250);
    return () => window.clearTimeout(t);
  }, [q, request.firm_key, request.cell_id, request.id]);
  const combine = async () => {
    if (!target) return;
    setBusy(true);
    try {
      await api.post(`${base(request.id)}/combine`, { target_id: target.id, reason: reason.trim() });
      navigate(`/admin/feedback/${target.id}`);
    } catch (e) { onError(msg(e, 'Could not combine these requests.')); } finally { setBusy(false); }
  };
  const undo = async (mergeId: string) => {
    try { await api.post(`/api/admin/feedback/merges/${mergeId}/undo`); await onChanged(); }
    catch (e) { onError(msg(e, 'Could not undo the combine.')); }
  };
  return (
    <Card className="flex flex-col gap-3 p-5 text-sm">
      <h2 className="m-0 text-[15px] font-bold">Duplicates</h2>
      {request.duplicates.map((d) => (
        <p key={d.id} className="m-0"><Link to={`/admin/feedback/${d.id}`} className="font-semibold text-brand">{d.reference}</Link> {d.title} · {d.submitted_by_name}</p>
      ))}
      {request.merges.filter((m) => m.can_undo).map((m) => (
        <div key={m.id} className="flex flex-col gap-1 rounded-[10px] bg-subtle px-3 py-2 text-[13px]">
          <div className="flex items-center gap-2">
            <span className="min-w-0 flex-1">{m.duplicate_reference} → {m.target_reference} by {m.merged_by_name}</span>
            <Button variant="link" size="sm" onClick={() => void undo(m.id)}><Bell aria-hidden size={14} /> Undo</Button>
          </div>
          <NotifyNote to={[`the people on ${m.duplicate_reference} and ${m.target_reference}`]} what="the requests are followed separately again" />
        </div>
      ))}
      {open ? (
        <>
          <p className="m-0 text-[13px] text-muted">Combine this into another request from the same firm. Its requester and notify list join that request's notify list. Undo within 7 days.</p>
          {target ? (
            <>
              <p className="m-0">Into <span className="font-semibold">{target.reference}</span> {target.title}{' '}
                <Button variant="link" size="sm" onClick={() => setTarget(null)}>Change</Button></p>
              <TextArea label="Why these are the same (the requester sees it)" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
              <NotifyNote to={[...audience(request, 'resolved'), `the people on ${target.reference}`]}
                what={<>{request.reference} closes as a duplicate with your reason, and {target.reference} gets a note that a request joined it</>} />
              <Button variant="primary" size="sm" disabled={busy || !reason.trim()} onClick={() => void combine()}><Bell aria-hidden size={14} /> Combine</Button>
            </>
          ) : (
            <>
              <TextField label="Find the other request" placeholder="FB- number or words" value={q} onChange={(e) => setQ(e.target.value)} small />
              {matches.length > 0 && (
                <ul className="m-0 list-none divide-y divide-line-soft rounded-[10px] border border-line p-0">
                  {matches.map((x) => (
                    <li key={x.id}>
                      <button type="button" onClick={() => setTarget(x)} className="w-full border-0 bg-transparent px-3 py-2 text-left text-[13px] hover:bg-row-hi">
                        <span className="font-semibold text-brand">{x.reference}</span> {x.title}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </>
      ) : request.status !== 'duplicate' && <p className="m-0 text-[13px] text-muted">Only open requests can be combined.</p>}
    </Card>
  );
}

/** Staff-only links between related requests from any firm. Awareness only: each request keeps its own
 * status and updates, nothing is merged and nobody is notified. */
function LinksPanel({ request, onSaved, onError }: { request: RequestDetail; onSaved: (r: RequestDetail) => void; onError: (m: string) => void }) {
  const [q, setQ] = useState('');
  const [matches, setMatches] = useState<QueueRow[]>([]);
  const [busy, setBusy] = useState(false);
  const linked = new Set(request.links.map((l) => l.id));
  useEffect(() => {
    if (!q.trim()) { setMatches([]); return; }
    const t = window.setTimeout(() => {
      const params = new URLSearchParams({ status: '', q: q.trim() });
      api.get<{ requests: QueueRow[] }>(`/api/admin/feedback?${params}`)
        .then((r) => setMatches(r.requests.filter((x) => x.id !== request.id).slice(0, 5)), () => setMatches([]));
    }, 250);
    return () => window.clearTimeout(t);
  }, [q, request.id]);
  const link = async (otherId: string) => {
    setBusy(true);
    try { onSaved((await api.post<{ request: RequestDetail }>(`${base(request.id)}/links`, { other_id: otherId })).request); setQ(''); }
    catch (e) { onError(msg(e, 'Could not link these requests.')); } finally { setBusy(false); }
  };
  const unlink = async (otherId: string) => {
    setBusy(true);
    try { onSaved((await api.del<{ request: RequestDetail }>(`${base(request.id)}/links/${otherId}`)).request); }
    catch (e) { onError(msg(e, 'Could not remove the link.')); } finally { setBusy(false); }
  };
  return (
    <Card className="flex flex-col gap-3 p-5 text-sm">
      <h2 className="m-0 text-[15px] font-bold">Linked requests</h2>
      <p className="m-0 text-[13px] text-muted">Tie the same problem together across firms, for the team's awareness. Each request keeps its own status and updates.</p>
      <NotifyNote to={null} what="Firms never see links" />
      {request.links.length > 0 && (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {request.links.map((l) => (
            <li key={l.id} className="flex items-start gap-2 rounded-[10px] bg-subtle px-3 py-2 text-[13px]">
              <span className="min-w-0 flex-1">
                <Link to={`/admin/feedback/${l.id}`} className="font-semibold text-brand">{l.reference}</Link> {l.title}
                <span className="block text-xs text-muted">{firmLabel(l)} · {STATUS_LABEL[l.status]} · linked by {l.linked_by_name}</span>
              </span>
              <Button variant="link" size="sm" disabled={busy} aria-label={`Unlink ${l.reference}`} onClick={() => void unlink(l.id)}><Unlink aria-hidden size={14} /> Unlink</Button>
            </li>
          ))}
        </ul>
      )}
      <TextField label="Find a related request" placeholder="FB- number or words, any firm" value={q} onChange={(e) => setQ(e.target.value)} small />
      {matches.length > 0 && (
        <ul className="m-0 list-none divide-y divide-line-soft rounded-[10px] border border-line p-0">
          {matches.map((x) => (
            <li key={x.id} className="flex items-center gap-2 px-3 py-2 text-[13px]">
              <span className="min-w-0 flex-1"><span className="font-semibold text-brand">{x.reference}</span> {x.title}
                <span className="block text-xs text-muted">{firmLabel(x)}</span></span>
              {linked.has(x.id)
                ? <span className="text-xs text-muted">Linked</span>
                : <Button variant="outline" size="sm" disabled={busy} aria-label={`Link ${x.reference}`} onClick={() => void link(x.id)}><Link2 aria-hidden size={14} /> Link</Button>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** The backlog item this request is on, or a way to put it on the backlog (linked, prefilled from the request). */
function BacklogPanel({ request, onChanged }: { request: RequestDetail; onChanged: () => void }) {
  const [open, setOpen] = useState(false);
  const r = request.release;
  return (
    <Card className="flex flex-col gap-2 p-5 text-sm">
      <h2 className="m-0 flex items-center gap-1.5 text-[15px] font-bold"><ListChecks aria-hidden size={16} /> Backlog</h2>
      {r ? (
        <>
          <Link to={`/admin/backlog/${r.item_id}`} className="font-semibold text-ink no-underline hover:underline"><span className="text-brand">{r.item_reference}</span> {r.item_title}</Link>
          <span className="text-[13px] text-muted">
            {!r.release_id ? 'Not scheduled yet'
              : r.release_kind === 'hotfix' ? <><Zap aria-hidden size={12} className="inline text-warn-fg" /> {r.release_status === 'shipped' ? `Live in ${r.release_name}; the firm hears with the next release` : 'In the next hotfix; the firm hears with the next release'}</>
              : <Link to={`/admin/releases/${r.release_id}`}>{r.release_name}</Link>}
          </span>
        </>
      ) : (
        <>
          <p className="m-0 text-[13px] text-muted">Not on the backlog yet. Add it to track the work and schedule it.</p>
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>Add to backlog</Button>
          {open && (
            <NewItemDialog open onClose={() => setOpen(false)} onCreated={() => { setOpen(false); onChanged(); }}
              defaults={{
                kind: request.kind === 'bug' ? 'bug_fix' : 'feature', title: request.title, area: request.area,
                request: { id: request.id, reference: request.reference, title: request.title, firm: firmLabel(request), status: request.status, estimate: request.estimated_completion },
              }} />
          )}
        </>
      )}
    </Card>
  );
}
